import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Input from '../../components/Input';
import PasswordInput from '../../components/PasswordInput';
import ProgressBar from '../../components/ProgressBar';
import Button from '../../components/Button';
import LocationInput from '../../components/LocationInput';
import {
    validateEmail,
    validatePassword,
    validateName,
    validateVisaType,
    validateLocation,
    validatePasswordMatch,
} from '../../utils/validation';
import type { SignupFormData } from '../../types/onboarding';
import { userService } from '../../api/userService';
import { apiPostPublic } from '../../api';
import { useUserStore, userToUserData } from '../../stores/userStore';
import { useOnboardingStore } from '../../stores/onboardingStore';
import logo from "../../assets/images/logo.png";

const VISA_TYPES = [
    { value: "H2B", label: "H-2B" },
    { value: "J1", label: "J-1" },
    { value: "H1B", label: "H-1B" },
    { value: "L1", label: "L-1" },
    { value: "O1", label: "O-1" },
    { value: "F1", label: "F-1 / OPT" },
    { value: "TN", label: "TN" },
    { value: "E2", label: "E-2" },
    { value: "H2A", label: "H-2A" },
    { value: "B1B2", label: "B-1 / B-2" },
    { value: "Other", label: "Other" },
];

const VISA_HISTORY_OPTIONS = [
    { value: "H2B", label: "H-2B" },
    { value: "J1", label: "J-1" },
    { value: "H1B", label: "H-1B" },
    { value: "OPT", label: "OPT" },
    { value: "TN", label: "TN" },
    { value: "L1", label: "L-1" },
    { value: "O1", label: "O-1" },
    { value: "never", label: "Never on a visa", labelKey: "signup.visa_never" },
    { value: "prefer_not_to_say", label: "Prefer not to say", labelKey: "signup.visa_prefer_not" },
];

/**
 * MergedSignupScreen - Screen 1 of the onboarding flow
 * Combines account creation with essential profile information
 */
const MergedSignupScreen: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Form state
    const [formData, setFormData] = useState<SignupFormData>({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        visaType: '',
        location: '',
        memberType: 'visa_worker',
        visaHistory: '',
        helperStatus: '',
        promoCode: '',
        dateOfBirth: '',
    });

    // Error state
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Submission state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    // Promo code validation state
    const [promoStatus, setPromoStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
    const [promoMessage, setPromoMessage] = useState<string>('');

    const validatePromoCode = async (code: string) => {
        const trimmed = code.trim();
        if (!trimmed) {
            setPromoStatus('idle');
            setPromoMessage('');
            return;
        }
        setPromoStatus('checking');
        try {
            const resp = await apiPostPublic<{ success: boolean; valid?: boolean; description?: string; message?: string; reason?: string }>(
                '/api/campaigns/promo-codes/validate',
                { code: trimmed }
            );
            if (resp.success && resp.valid) {
                setPromoStatus('valid');
                setPromoMessage(resp.description || t('signup.promo_applied'));
            } else {
                setPromoStatus('invalid');
                setPromoMessage(
                    resp.reason === 'max_redemptions_reached'
                        ? t('signup.promo_max_uses')
                        : t('signup.promo_invalid')
                );
            }
        } catch (_err) {
            setPromoStatus('invalid');
            setPromoMessage(t('signup.promo_invalid'));
        }
    };

    /**
     * Handle input changes
     */
    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    /**
     * Validate all form fields
     */
    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        // Validate first name
        const firstNameError = validateName(formData.firstName, t('signup.first_name_label'));
        if (firstNameError) newErrors.firstName = firstNameError;

        // Validate last name
        const lastNameError = validateName(formData.lastName, t('signup.last_name_label'));
        if (lastNameError) newErrors.lastName = lastNameError;

        // Validate email
        const emailError = validateEmail(formData.email);
        if (emailError) newErrors.email = emailError;

        // Validate password
        const passwordError = validatePassword(formData.password);
        if (passwordError) newErrors.password = passwordError;

        // Validate password match
        const passwordMatchError = validatePasswordMatch(formData.password, formData.confirmPassword);
        if (passwordMatchError) newErrors.confirmPassword = passwordMatchError;

        // Validate visa type (only required for visa workers)
        if (formData.memberType !== 'community_helper') {
            const visaTypeError = validateVisaType(formData.visaType);
            if (visaTypeError) newErrors.visaType = visaTypeError;
        }

        // Validate location
        const locationError = validateLocation(formData.location);
        if (locationError) newErrors.location = locationError;

        // Validate date of birth — required, must be 18+
        if (!formData.dateOfBirth) {
            newErrors.dateOfBirth = t('about_you.dob_required');
        } else {
            const dob = new Date(formData.dateOfBirth);
            const today = new Date();
            let age = today.getFullYear() - dob.getFullYear();
            const m = today.getMonth() - dob.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
            if (age < 18) newErrors.dateOfBirth = t('about_you.dob_under_18');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Handle form submission
     */
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        // Validate form
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            // Call registration API
            const response = await userService.registerUser({
                ...formData,
                visaType: formData.memberType === 'community_helper' ? '' : formData.visaType,
                memberType: formData.memberType,
                visaHistory: formData.memberType === 'community_helper' ? formData.visaHistory : undefined,
                helperStatus: formData.memberType === 'community_helper' ? formData.helperStatus : undefined,
            });

            if (response.success && response.user && response.token) {
                // Convert API user to UserData format for store
                const userData = userToUserData(response.user);

                // Store user data and token in auth store
                const userStore = useUserStore.getState();
                userStore.setUser(userData);
                userStore.setToken(response.token);

                // Store signup data in onboarding store for profile completion tracking
                const onboardingStore = useOnboardingStore.getState();
                onboardingStore.setSignupData(formData);

                // Show success toast
                toast.success(t('signup.account_created'));

                // Navigate to tips-trips-advice for soft landing
                setTimeout(() => {
                    navigate('/optional-profile');
                }, 500);
            } else {
                throw new Error(response.message || t('signup.registration_failed'));
            }
        } catch (error: any) {
            console.error('Signup error:', error);

            // Parse error message
            let errorMessage = t('signup.generic_error');

            if (error.message) {
                errorMessage = error.message;

                // Handle specific error cases
                if (error.message.includes('email') && error.message.includes('already')) {
                    setErrors({ email: t('signup.email_already_registered') });
                    errorMessage = t('signup.email_already_registered_full');
                } else if (error.message.includes('Invalid email')) {
                    setErrors({ email: t('signup.err_email_format') });
                } else if (error.message.includes('password')) {
                    setErrors({ password: error.message });
                }
            }

            // Show error toast
            toast.error(errorMessage);

            // Set general error if no specific field error
            if (Object.keys(errors).length === 0) {
                setErrors({ submit: errorMessage });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pt-8 overflow-y-auto">
            <div className="max-w-2xl mx-auto px-4">
                <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                    {/* Progress Bar */}
                    <div className="mb-6">
                        <ProgressBar currentStep={1} totalSteps={3} />
                    </div>

                    {/* Logo and Header */}
                    <div className="text-center mb-6 flex flex-col items-center justify-center">
                        <div className=" flex items-center justify-center max-w-[300px]">
                            <img src={logo} alt="Logo" className="" />
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('signup.create_account')}</h1>
                        <p className="text-sm text-gray-600">{t('signup.step_1_of_3')}</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm">
                        <div className="space-y-4">
                            {/* First Name and Last Name - 2 column grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                    label={t('signup.first_name')}
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    error={errors.firstName}
                                    placeholder={t('signup.first_name_placeholder')}
                                    required
                                />
                                <Input
                                    label={t('signup.last_name')}
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    error={errors.lastName}
                                    placeholder={t('signup.last_name_placeholder')}
                                    required
                                />
                            </div>

                            {/* Email */}
                            <Input
                                label={t('signup.email')}
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                error={errors.email}
                                placeholder={t('signup.email_example_placeholder')}
                                required
                            />

                            {/* Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                    {t('signup.password')}
                                </label>
                                <PasswordInput
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    error={errors.password}
                                    placeholder={t('signup.enter_password')}
                                    password={formData.password}
                                    showRequirements={true}
                                    required
                                />
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                    {t('signup.confirm_password')}
                                </label>
                                <PasswordInput
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    error={errors.confirmPassword}
                                    placeholder={t('signup.confirm_password_placeholder')}
                                    password={formData.confirmPassword}
                                    showRequirements={false}
                                    required
                                />
                            </div>

                            {/* Member Type Toggle */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                    {t('signup.i_am')}
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setFormData((prev) => ({ ...prev, memberType: 'visa_worker', visaHistory: '', helperStatus: '' }))}
                                        className={`h-[50px] rounded-xl border text-sm font-semibold transition-colors ${
                                            formData.memberType === 'visa_worker'
                                                ? 'border-[#00A8E8] bg-blue-50 text-[#00A8E8]'
                                                : 'border-gray-200 bg-gray-100 text-gray-600 hover:border-gray-400'
                                        }`}
                                    >
                                        {t('signup.im_on_a_visa')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData((prev) => ({ ...prev, memberType: 'community_helper', visaType: '' }))}
                                        className={`h-[50px] rounded-xl border text-sm font-semibold transition-colors ${
                                            formData.memberType === 'community_helper'
                                                ? 'border-amber-500 bg-amber-50 text-amber-700'
                                                : 'border-gray-200 bg-gray-100 text-gray-600 hover:border-gray-400'
                                        }`}
                                    >
                                        {t('signup.im_here_to_help')}
                                    </button>
                                </div>
                            </div>

                            {/* Visa worker: visa type dropdown */}
                            {formData.memberType === 'visa_worker' && (
                                <div>
                                    <div className="flex items-center gap-1 mb-1">
                                        <label htmlFor="visaType" className="block text-sm font-medium text-gray-700">
                                            {t('signup.visa_type')}
                                        </label>
                                        <svg
                                            className="w-4 h-4 text-gray-400 cursor-help"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                            aria-label={t('signup.visa_type_help_aria')}
                                        >
                                            <title>{t('signup.visa_types_title')}</title>
                                            <path
                                                fillRule="evenodd"
                                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </div>
                                    <select
                                        id="visaType"
                                        name="visaType"
                                        value={formData.visaType}
                                        onChange={handleChange}
                                        className={`
                                        w-full px-4 py-3
                                        bg-gray-100
                                        text-gray-900 text-base
                                        rounded-xl
                                        border-0
                                        focus:outline-none focus:ring-2 focus:ring-[#00A8E8]
                                        transition-all duration-200
                                        ${errors.visaType ? 'ring-2 ring-red-500' : ''}
                                        `}
                                        required
                                    >
                                        <option value="">{t('signup.select_visa_type')}</option>
                                        {VISA_TYPES.map((v) => (
                                            <option key={v.value} value={v.value}>{v.label}</option>
                                        ))}
                                    </select>
                                    {errors.visaType && (
                                        <p className="mt-1 text-sm text-red-500">{errors.visaType}</p>
                                    )}
                                </div>
                            )}

                            {/* Community helper: visa history + helper status */}
                            {formData.memberType === 'community_helper' && (
                                <>
                                    <div>
                                        <label htmlFor="visaHistory" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                            {t('signup.ever_on_us_visa')}
                                        </label>
                                        <select
                                            id="visaHistory"
                                            name="visaHistory"
                                            value={formData.visaHistory}
                                            onChange={handleChange}
                                            className={`
                                            w-full px-4 py-3
                                            bg-gray-100
                                            text-gray-900 text-base
                                            rounded-xl
                                            border-0
                                            focus:outline-none focus:ring-2 focus:ring-[#00A8E8]
                                            transition-all duration-200
                                            ${!formData.visaHistory ? 'text-gray-500' : ''}
                                            `}
                                        >
                                            <option value="" disabled>{t('signup.ever_on_us_visa')}</option>
                                            {VISA_HISTORY_OPTIONS.map((v: any) => (
                                                <option key={v.value} value={v.value}>{v.labelKey ? t(v.labelKey) : v.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="helperStatus" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                            {t('signup.tell_us_about_you')}
                                        </label>
                                        <textarea
                                            id="helperStatus"
                                            name="helperStatus"
                                            value={formData.helperStatus}
                                            onChange={handleChange}
                                            maxLength={200}
                                            placeholder={t('signup.helper_placeholder')}
                                            className="w-full px-4 py-3 bg-gray-100 text-gray-900 text-base rounded-xl border-0 focus:outline-none focus:ring-2 focus:ring-[#00A8E8] transition-all duration-200 h-[80px] resize-none"
                                        />
                                        <p className="mt-0.5 text-xs text-gray-400 text-right">{formData.helperStatus?.length || 0}/200</p>
                                    </div>
                                </>
                            )}
                            <div className="">
                                <label htmlFor="location" className="block text-sm font-medium text-gray-700 text-left pb-2">
                                    {t('signup.current_location')}
                                </label>
                                {/* Current Location */}
                                <div className="relative">
                                    <LocationInput
                                        value={formData.location}
                                        onChange={(location) => {
                                            setFormData(prev => ({ ...prev, location: location.address }));
                                            if (errors.location) {
                                                setErrors(prev => {
                                                    const newErrors = { ...prev };
                                                    delete newErrors.location;
                                                    return newErrors;
                                                });
                                            }
                                        }}
                                        placeholder={t('signup.location_example_placeholder')}
                                        required
                                    />
                                    {errors.location && (
                                        <p className="mt-1 text-sm text-red-500">{errors.location}</p>
                                    )}
                                </div>
                            </div>

                            {/* Date of Birth */}
                            <div>
                                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                    {t('about_you.date_of_birth')} <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="dateOfBirth"
                                    name="dateOfBirth"
                                    type="date"
                                    max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().split('T')[0]; })()}
                                    value={formData.dateOfBirth}
                                    onChange={(e) => {
                                        setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }));
                                        if (errors.dateOfBirth) setErrors(prev => { const n = { ...prev }; delete n.dateOfBirth; return n; });
                                    }}
                                    className={`w-full px-4 py-3 bg-gray-100 text-gray-900 text-base rounded-xl border ${
                                        errors.dateOfBirth ? 'border-red-500' : 'border-0'
                                    } focus:outline-none focus:ring-2 focus:ring-[#00A8E8] transition-all duration-200`}
                                />
                                {errors.dateOfBirth && (
                                    <p className="mt-1 text-sm text-red-500">{errors.dateOfBirth}</p>
                                )}
                            </div>

                            {/* Promo Code (optional) */}
                            <div>
                                <label htmlFor="promoCode" className="block text-sm font-medium text-gray-700 mb-1 text-left">
                                    Promo Code (optional)
                                </label>
                                <input
                                    id="promoCode"
                                    name="promoCode"
                                    type="text"
                                    value={formData.promoCode || ''}
                                    onChange={(e) => {
                                        setFormData((prev) => ({ ...prev, promoCode: e.target.value }));
                                        if (promoStatus !== 'idle') {
                                            setPromoStatus('idle');
                                            setPromoMessage('');
                                        }
                                    }}
                                    onBlur={(e) => validatePromoCode(e.target.value)}
                                    placeholder={t('signup.promo_placeholder')}
                                    className="w-full px-4 py-3 bg-gray-100 text-gray-900 text-base rounded-xl border-0 focus:outline-none focus:ring-2 focus:ring-[#00A8E8] transition-all duration-200"
                                />
                                {promoStatus === 'checking' && (
                                    <p className="mt-1 text-xs text-gray-500">{t('signup.promo_checking')}</p>
                                )}
                                {promoStatus === 'valid' && (
                                    <p className="mt-1 text-sm text-green-600">✓ {promoMessage}</p>
                                )}
                                {promoStatus === 'invalid' && (
                                    <p className="mt-1 text-sm text-red-500">{promoMessage || t('signup.promo_invalid')}</p>
                                )}
                            </div>

                            {/* Submit Error */}
                            {errors.submit && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
                                    <p className="text-sm text-red-600">{errors.submit}</p>
                                </div>
                            )}

                            {/* Terms & Conditions */}
                            <div className="flex items-start gap-3 mb-4">
                                <input
                                    type="checkbox"
                                    id="terms-agree"
                                    checked={agreedToTerms}
                                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                                    className="mt-1 h-4 w-4 cursor-pointer accent-blue-600"
                                />
                                <label htmlFor="terms-agree" className="text-sm text-gray-500 leading-relaxed">
                                    {t('signup.agree_to_terms')}{' '}
                                    <a href="/#/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-700">
                                        {t('signup.terms_of_service')}
                                    </a>
                                    {' '}{t('signup.and')}{' '}
                                    <a href="/#/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-700">
                                        {t('signup.privacy_policy')}
                                    </a>
                                </label>
                            </div>

                            {/* Continue Button */}
                            <div className="pt-2">
                                <Button
                                    type="submit"
                                    disabled={isSubmitting || !agreedToTerms}
                                    className="bg-[#00A8E8] hover:bg-[#0080B8] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? t('signup.creating_account') : t('common.continue')}
                                </Button>
                            </div>
                        </div>
                    </form>

                    {/* Already have an account */}
                    <p className="text-center text-sm text-gray-600 mt-4">
                        {t('signup.already_have_account')}{' '}
                        <button onClick={() => navigate('/sign-in')} className="text-[#00A8E8] hover:underline font-medium">
                            {t('common.sign_in')}
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MergedSignupScreen;
